import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Form, Input, Space, notification, Typography, Row, Col, Select, Switch, Radio, Tooltip, Alert } from 'antd';
import { ArrowLeftOutlined, QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createDocumentTemplate } from '../../actions/documentTemplateActions';
import { DOCUMENT_TEMPLATE_CREATE_RESET } from '../../constants/documentTemplateConstants';
import { useTranslation } from 'react-i18next';
import PublicTemplateBrowser from '../../components/documentTemplate/PublicTemplateBrowser.component';

const { Title } = Typography;
const { TextArea } = Input;

const SPECIALTIES = [
  'Cardiology',
  'Dermatology',
  'Emergency Medicine',
  'Endocrinology',
  'Family Medicine',
  'Gastroenterology',
  'General Surgery',
  'Hematology',
  'Internal Medicine',
  'Neurology',
  'Obstetrics and Gynecology',
  'Oncology',
  'Ophthalmology',
  'Orthopedics',
  'Pediatrics',
  'Psychiatry',
  'Pulmonology',
  'Radiology',
  'Rheumatology',
  'Urology'
];

const DocumentTemplateCreateScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  const [contentType, setContentType] = useState('raw');
  const [browserVisible, setBrowserVisible] = useState(false);
  const [baseTemplateId, setBaseTemplateId] = useState(null);

  const documentTemplateCreate = useSelector(state => state.documentTemplateCreate);
  const { loading, error, success, documentTemplate } = documentTemplateCreate;

  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  useEffect(() => {
    if (success && documentTemplate?.id) {
      api.success({
        message: t('Success'),
        description: t('Template created successfully'),
        placement: 'bottom'
      });
      dispatch({ type: DOCUMENT_TEMPLATE_CREATE_RESET });
      navigate(`/document_templates/${documentTemplate.id}/edit`);
    }
    if (error) {
      api.error({
        message: t('Error'),
        description: error,
        placement: 'bottom'
      });
      dispatch({ type: DOCUMENT_TEMPLATE_CREATE_RESET });
    }
  }, [success, error, documentTemplate, api, dispatch, navigate, t]);

  const onFinish = (values) => {
    dispatch(createDocumentTemplate({
      ...values,
      baseTemplateId: baseTemplateId,
      tags: values.tags ? values.tags.split(',').map(tag => tag.trim()) : [],
      lastUsedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      identityUserId: userInfo.id
    }));
  };

  const handleSelectTemplate = (template) => {
    form.setFieldsValue({
      name: template.name,
      language: template.language,
      specialty: template.specialty,
      rawContent: template.rawContent,
      htmlContent: template.htmlContent,
      structure: template.structure,
      exampleNote: template.exampleNote,
      howToUse: template.howToUse,
      tags: template.tags?.join(', '),
      isPublic: false,
    });
    setBaseTemplateId(template.id);
    setContentType(template.htmlContent ? 'html' : 'raw');
  };

  return (
    <Row gutter={[16, 16]} style={{ padding: "16px" }}>
      <Col xs={24}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate(-1)}
          >
            {t('Return back')}
          </Button>
          <Title level={2}>{t('Create Document Template')}</Title>
        </Space>
      </Col>

      <Col xs={24} md={16} lg={12}>
        {contextHolder}
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>New Template</span>
              <Button 
                type="primary" 
                icon={<SearchOutlined />} 
                onClick={() => setBrowserVisible(true)}
              >
                {t('Browse Public Templates')}
              </Button>
            </div>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            size="large"
            initialValues={{
              language: 'en',
              isPublic: false,
              usageCount: 0,
              contentType: 'raw'
            }}
          >
            <Form.Item
              label={
                <Space>
                  {t('Name')}
                  <Tooltip title={t('The name of the template that will be displayed in the list')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="name"
              rules={[{ required: true, message: t('Name is required') }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  {t('Language')}
                  <Tooltip title={t('The primary language of this template')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="language"
              rules={[{ required: true, message: t('Language is required') }]}
            >
              <Select>
                <Select.Option value="en">English</Select.Option>
                <Select.Option value="fr">French</Select.Option>
                <Select.Option value="es">Spanish</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  {t('Specialty')}
                  <Tooltip title={t('The medical specialty this template is designed for')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="specialty"
              rules={[{ required: true, message: t('Specialty is required') }]}
            >
              <Select
                showSearch
                placeholder="Select a specialty"
                optionFilterProp="children"
              >
                {SPECIALTIES.map(specialty => (
                  <Select.Option key={specialty} value={specialty}>
                    {specialty}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  {t('Content Type')}
                  <Tooltip title={t('Choose between raw text or HTML formatted content')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
            >
              <Radio.Group value={contentType} onChange={e => setContentType(e.target.value)}>
                <Radio.Button value="raw">Raw Content</Radio.Button>
                <Radio.Button value="html">HTML Content</Radio.Button>
              </Radio.Group>
            </Form.Item>

            {contentType === 'raw' && (
              <Form.Item
                label={
                  <Space>
                    {t('Raw Content')}
                    <Tooltip title={t('The template content in raw text format')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="rawContent"
              >
                <TextArea rows={8} />
              </Form.Item>
            )}

            {contentType === 'html' && (
              <Form.Item
                label={
                  <Space>
                    {t('HTML Content')}
                    <Tooltip title={t('The template content in HTML format')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="htmlContent"
              >
                <TextArea rows={6} />
              </Form.Item>
            )}

            <Form.Item
              label={
                <Space>
                  {t('Structure')}
                  <Tooltip title={t('The structural format or schema of the template')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="structure"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  {t('Example Note')}
                  <Tooltip title={t('An example of how this template should be used')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="exampleNote"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  {t('How To Use')}
                  <Tooltip title={t('Instructions for using this template')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="howToUse"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  {t('Tags')}
                  <Tooltip title={t('Comma-separated tags to help categorize and find this template')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="tags"
            >
              <Input placeholder="tag1, tag2, tag3" />
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  {t('Is Public')}
                  <Tooltip title={t('Make this template available to all users')}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="isPublic"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch />
            </Form.Item>

            <Form.Item>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {t('Create')}
                  </Button>
                  <Button onClick={() => navigate(-1)}>
                    {t('Cancel')}
                  </Button>
                </Space>
                {success && (
                  <Alert
                    message={t('Success')}
                    description={t('Template created successfully')}
                    type="success"
                    showIcon
                  />
                )}
                {error && (
                  <Alert
                    message={t('Error')}
                    description={error}
                    type="error"
                    showIcon
                  />
                )}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Col>

      <PublicTemplateBrowser
        visible={browserVisible}
        onClose={() => setBrowserVisible(false)}
        onSelectTemplate={handleSelectTemplate}
      />
    </Row>
  );
};

export default DocumentTemplateCreateScreen; 